import { useMemo } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { blips, school, tally, type Blip, type Fish, type SonarItem } from '../sonar'
import { usePolling } from '../hooks/useAsync'
import { ErrorBanner, Spinner } from './common'
import { t } from '../i18n/lingua'

/**
 * Il sonar: il sottomarino guarda sotto di sé, e ciò che non ha ancora
 * riconosciuto lampeggia.
 *
 * ## A cosa serve
 *
 * Quando un parco è a regime, la dashboard è una schermata di numeri che non
 * cambiano. Un CI comparso un'ora fa — da una scansione o da una conversazione
 * NetFlow — è una riga in più in un elenco lungo, e nessuno la nota. Qui è un
 * pesce rosso che lampeggia in mezzo a pesci verdi.
 *
 * ## Perché una scena e non solo il quadro tondo
 *
 * Il quadro dice **dove**; la scena dice **quanto è popolato là sotto** e come
 * si muove. Restano entrambi, e non è decorazione ridondante: il quadro è lo
 * strumento, la scena è ciò che lo strumento sta guardando. Un puntino fermo su
 * un cerchio non comunica «c'è vita, e una parte non la conosco» — un banco sì.
 *
 * ## Il rosso è prenotato
 *
 * Nelle immagini di sonar gli impulsi sono gialli e rossi. Qui gli impulsi sono
 * azzurri: rosso e verde in questo quadro vogliono dire **una cosa sola** — non
 * riconosciuto, riconosciuto — e un impulso rosso che attraversa la scena ogni
 * tre secondi renderebbe quel significato illeggibile proprio mentre lo si
 * cerca.
 *
 * ## Il movimento è una scelta con un costo, quindi ha dei limiti
 *
 * Una cosa che lampeggia è fastidiosa per progetto: è il suo mestiere. Ma il
 * fastidio non deve diventare un problema di salute né un rumore permanente,
 * quindi:
 *
 * - **si accende a mano**. Non parte da solo all'apertura della dashboard;
 * - **rispetta `prefers-reduced-motion`**: chi ha chiesto al proprio sistema di
 *   non animare le interfacce lo ha chiesto sul serio — c'è chi dalle immagini
 *   lampeggianti sta male. In quel caso la scena si ferma tutta, i pesci restano
 *   dove sono e i nuovi tengono un anello marcato. L'informazione non si perde,
 *   il movimento sì;
 * - **il colore non è mai solo**: sotto c'è il conteggio scritto, ogni pesce
 *   dice al passaggio del mouse che cos'è e perché è rosso, e i nuovi sono
 *   elencati per nome. Rosso e verde sono la coppia che più persone confondono,
 *   e questo quadro sarebbe illeggibile proprio per loro.
 */
/**
 * Il parco visto dai due quadri: una lettura sola, condivisa.
 *
 * `Date.now()` viene letto **una volta**: con due letture un CI comparso sul
 * filo delle 24 ore potrebbe risultare nuovo in un quadro e catalogato
 * nell'altro — la stessa cosa, due colori, nella stessa schermata.
 */
function useParco() {
  // Si ricarica da solo: il punto di guardare il sonar è vedere comparire
  // qualcosa mentre lo si guarda.
  const { data, error, loading } = usePolling(() => api.cis({ limit: 300 }), 15000)
  const items = (data ?? []) as SonarItem[]
  const now = data ? Date.now() : 0
  const points = useMemo(() => blips(items, now), [data, now])
  const banco = useMemo(() => school(items, now), [data, now])
  return { data, error, loading, points, banco, counts: tally(points) }
}

export default function Sonar({ onClose }: { onClose: () => void }) {
  const { data, error, loading, points, banco, counts } = useParco()
  const { principal } = useAuth()

  return (
    <div className="sonar">
      <div className="sea" aria-hidden="true">
        {/* La luce che filtra dalla superficie. Due strati che scorrono a
            velocità diverse: è ciò che fa sembrare l'acqua acqua invece di un
            fondo azzurro. */}
        <span className="sea__light sea__light--1" />
        <span className="sea__light sea__light--2" />

        <div className="sea__vessel">
          <Submarine />
          {/* Il sottomarino è l'azienda, quindi porta il suo nome. */}
          <span className="sea__vessel-name">{principal?.tenant_name ?? 'CENSO'}</span>
        </div>

        {/* Gli impulsi: partono da sotto lo scafo e si allargano verso il
            fondo. Quattro, sfasati, così ce n'è sempre uno in viaggio. */}
        <span className="sea__ping sea__ping--1" />
        <span className="sea__ping sea__ping--2" />
        <span className="sea__ping sea__ping--3" />
        <span className="sea__ping sea__ping--4" />

        {banco.fish.map((fish) => (
          <FishMark key={fish.id} fish={fish} />
        ))}

        {BUBBLES.map((bubble) => (
          <span
            key={bubble.left}
            className="sea__bubble"
            style={
              {
                left: `${bubble.left}%`,
                '--size': `${bubble.size}px`,
                '--dur': `${bubble.duration}s`,
                '--delay': `${bubble.delay}s`,
              } as React.CSSProperties
            }
          />
        ))}

        <span className="sea__deep" />

        <div className="sea__bed">
          <svg className="sea__rocks sea__rocks--far" viewBox="0 0 1200 170" preserveAspectRatio="none">
            <path d="M0 62 L96 30 L188 58 L268 22 L354 54 L448 26 L536 60 L624 28 L722 56 L812 24 L900 52 L994 30 L1092 58 L1200 34 L1200 170 L0 170 Z" />
          </svg>
          <svg className="sea__rocks" viewBox="0 0 1200 170" preserveAspectRatio="none">
            <path d="M0 96 L74 66 L148 88 L214 54 L296 84 L360 62 L432 92 L498 70 L560 100 L640 58 L714 86 L790 48 L862 82 L936 60 L1010 92 L1086 64 L1146 90 L1200 70 L1200 170 L0 170 Z" />
          </svg>
          {WEEDS.map((weed) => (
            <span
              key={weed.left}
              className="sea__weed"
              style={
                {
                  left: `${weed.left}%`,
                  '--h': `${weed.height}px`,
                  '--delay': `${weed.delay}s`,
                } as React.CSSProperties
              }
            />
          ))}
        </div>
      </div>

      {loading && !data ? <Spinner label={t('Ascolto…')} /> : null}
      <ErrorBanner error={error} />

      <Readout points={points} counts={counts}>
        {banco.hidden > 0 && (
          // Un banco tagliato in silenzio si legge come tutto il parco.
          <p className="field__hint" style={{ margin: '4px 0 0' }}>
            {t('Nella scena ne nuotano {n}: gli altri {nascosti} ci sono, ma disegnarli tutti farebbe muovere l’immagine a scatti. I nuovi non vengono mai nascosti per primi.', { n: banco.fish.length, nascosti: banco.hidden })}
          </p>
        )}
      </Readout>

      <p className="field__hint" style={{ marginBottom: 0 }}>
        {t('Rosso è ciò che la piattaforma non ha ancora riconosciuto — non classificato, non gestito, o comparso nelle ultime 24 ore. La scena legge fino a 300 CI.')}{' '}
        <button className="linklike" onClick={onClose}>
          {t('spegni il sonar')}
        </button>
      </p>
    </div>
  )
}

/**
 * Il radar: lo strumento, senza la scena.
 *
 * È l'altra metà, e sta da sola perché risponde a un'altra domanda. Il sonar fa
 * vedere **quanto è popolato** là sotto e chiede di essere guardato per qualche
 * secondo; il radar è un colpo d'occhio — quanti punti, quanti rossi, dove — e
 * disegna **tutti** i CI letti, non i primi quarantotto: non deve muovere
 * niente, quindi non paga il costo che obbliga la scena a fermarsi.
 */
export function Radar({ onClose }: { onClose: () => void }) {
  const { data, error, loading, points, counts } = useParco()

  return (
    <div className="sonar">
      {loading && !data ? <Spinner label={t('Ascolto…')} /> : null}
      <ErrorBanner error={error} />

      <div className="sonar__scope sonar__scope--solo" aria-hidden="true">
        <span className="sonar__ring sonar__ring--1" />
        <span className="sonar__ring sonar__ring--2" />
        <span className="sonar__ring sonar__ring--3" />
        <span className="sonar__cross" />
        <span className="sonar__sweep" />
        {points.map((blip: Blip) => (
          <span
            key={blip.id}
            className={`sonar__blip sonar__blip--${blip.state}`}
            style={{ left: `${blip.x}%`, top: `${blip.y}%` }}
            title={`${blip.label} — ${blip.why}`}
          />
        ))}
      </div>

      <Readout points={points} counts={counts} />

      <p className="field__hint" style={{ marginBottom: 0 }}>
        {t('I punti più vicini al centro sono quelli da riconoscere: è dove va l’occhio per primo. Il quadro mostra fino a 300 CI.')}{' '}
        <button className="linklike" onClick={onClose}>
          {t('spegni il radar')}
        </button>
      </p>
    </div>
  )
}

/**
 * I conteggi e i nomi: la parte **leggibile** di entrambi i quadri.
 *
 * Sta qui, e non dentro l'uno o l'altro, perché è l'unica parte che uno screen
 * reader può leggere: il cerchio e la scena sono `aria-hidden`, e fingere il
 * contrario sarebbe peggio che dichiararlo. Sarebbe potuta finire duplicata nei
 * due componenti — ed è esattamente il modo in cui, alla prima modifica, i due
 * quadri cominciano a raccontare due cose diverse.
 */
function Readout({
  points,
  counts,
  children,
}: {
  points: Blip[]
  counts: { nuovi: number; noti: number }
  children?: React.ReactNode
}) {
  return (
    <div className="sonar__readout">
      <p className="sonar__tally">
        <span className="sonar__dot sonar__dot--nuovo" />
        <strong>{counts.nuovi}</strong> {t('da riconoscere')}
        <span className="sonar__dot sonar__dot--noto" />
        <strong>{counts.noti}</strong> {t('catalogati')}
      </p>

      {children}

      {counts.nuovi > 0 && (
        <ul className="sonar__list">
          {points
            .filter((b) => b.state === 'nuovo')
            .slice(0, 8)
            .map((blip) => (
              <li key={blip.id}>
                <Link to={`/cis/${blip.id}`}>{blip.label}</Link>{' '}
                <span className="text-muted">— {blip.why}</span>
              </li>
            ))}
          {counts.nuovi > 8 && (
            // Un elenco tagliato in silenzio si legge come completo.
            <li className="text-muted">{t('e altri {n}', { n: counts.nuovi - 8 })}</li>
          )}
        </ul>
      )}
    </div>
  )
}

/** Un CI che nuota. La sagoma è una sola: a distinguerli è il colore e il moto. */
function FishMark({ fish }: { fish: Fish }) {
  return (
    <span
      className={`sea__fish sea__fish--${fish.state} sea__fish--${
        fish.facing === 1 ? 'dx' : 'sx'
      }`}
      style={
        {
          top: `${fish.depth}%`,
          left: `${fish.x}%`,
          '--dur': `${fish.duration}s`,
          '--delay': `${fish.delay}s`,
          '--scale': fish.scale,
        } as React.CSSProperties
      }
      title={`${fish.label} — ${fish.why}`}
    >
      <span className="sea__fish-flip">
        <svg className="sea__fish-svg" viewBox="0 0 36 20">
          <path className="sea__fish-tail" d="M2 10 L11 4 L11 16 Z" />
          <path d="M11 10 C11 4.5 17 2.5 23 2.5 C29.5 2.5 34 6 34 10 C34 14 29.5 17.5 23 17.5 C17 17.5 11 15.5 11 10 Z" />
          <path className="sea__fish-fin" d="M19 3 L24 0.5 L25 3.5 Z" />
          <circle className="sea__fish-eye" cx="29" cy="8" r="1.4" />
        </svg>
      </span>
    </span>
  )
}

/** Il sottomarino: la piattaforma che sta ascoltando. */
function Submarine() {
  return (
    <svg className="sea__sub" viewBox="0 0 210 84">
      {/* elica */}
      <g className="sea__sub-screw">
        <path d="M12 42 L2 30 L2 54 Z" />
      </g>
      {/* timone di coda */}
      <path className="sea__sub-fin" d="M30 42 L12 24 L12 60 Z" />
      <path className="sea__sub-fin" d="M40 30 L34 10 L48 24 Z" />
      {/* scafo */}
      <path
        className="sea__sub-hull"
        d="M30 42 C30 26 66 18 116 18 C160 18 190 27 204 42 C190 57 160 66 116 66 C66 66 30 58 30 42 Z"
      />
      {/* torretta e periscopio */}
      <path className="sea__sub-hull" d="M96 18 L102 6 L134 6 L138 18 Z" />
      <rect className="sea__sub-hull" x="116" y="0" width="3" height="7" />
      <circle className="sea__sub-glass" cx="96" cy="42" r="5" />
      <circle className="sea__sub-glass" cx="118" cy="42" r="5" />
      <circle className="sea__sub-glass" cx="140" cy="42" r="5" />
      <circle className="sea__sub-glass" cx="162" cy="42" r="5" />
      {/* il trasduttore da cui parte l'impulso */}
      <circle className="sea__sub-dome" cx="117" cy="64" r="6" />
    </svg>
  )
}

// Bolle e alghe: sagome fisse, non estratte a caso. Il motivo è lo stesso delle
// posizioni dei pesci — la dashboard si ricarica da sola, e uno sfondo che si
// ridisegna diverso a ogni giro fa sembrare che sia cambiato qualcosa.
const BUBBLES = [
  { left: 12, size: 5, duration: 9, delay: 0 },
  { left: 27, size: 3, duration: 12, delay: 2.5 },
  { left: 46, size: 6, duration: 8, delay: 5 },
  { left: 63, size: 4, duration: 11, delay: 1.2 },
  { left: 78, size: 3, duration: 10, delay: 3.8 },
  { left: 91, size: 5, duration: 13, delay: 6.4 },
]

const WEEDS = [
  { left: 6, height: 46, delay: 0 },
  { left: 9, height: 30, delay: 1.1 },
  { left: 31, height: 38, delay: 0.6 },
  { left: 34, height: 54, delay: 2.2 },
  { left: 57, height: 34, delay: 1.6 },
  { left: 72, height: 50, delay: 0.3 },
  { left: 75, height: 28, delay: 2.8 },
  { left: 94, height: 42, delay: 1.9 },
]
